import { connect } from 'react-redux';
import {
    injectIntl,
    FormattedMessage,
    WrappedComponentProps
} from 'react-intl';
// eslint-disable-next-line import/no-duplicates
import { differenceInDays, format } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import deLocale from 'date-fns/locale/de';

import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import { SHOW_ACCOUNTS_SCHEDULED_REPAYMENTS_MODAL } from '../../../modals/repaymentScheduleModal/RepaymentScheduleModal.actions';

import DashboardCTA from '../DashboardCTA/DashboardCTA';
import DateInIcon from './DateInIcon';

import messages from './FutureRepayments.messages';
import { RootState } from '@iwProvider/configureStore';
import { Dispatch } from 'redux';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@iwoca/orion';
import { TProductType } from '../../../../myAccount/dashboard/Dashboard.types';
import { tracking } from '@iwoca/frontend-tracking-library';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { getCountry } from '@iwIntlProvider/CountryProvider';

const mapStateToProps = (state: RootState) => ({
    locale: state.siteContext.locale
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    showSchedule: (productType: TProductType) =>
        dispatch({
            type: SHOW_ACCOUNTS_SCHEDULED_REPAYMENTS_MODAL,
            productType
        })
});

type InjectedProps = WrappedComponentProps &
    ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> & {
        className?: string;
        accountId: string;
    };

const FutureRepayments = ({
    showSchedule,
    locale,
    className,
    accountId
}: InjectedProps) => {
    const stateKey = useStateKey();

    const {
        data: getAccountsResponse,
        isLoading,
        isError
    } = useQuery(getAccountsQueryConfig(stateKey));

    if (isLoading || isError) return null;

    const account = getAccountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const productType = account.product_type!;

    const nextRepaymentDate = new Date(
        account.scheduled_payments!.schedule![0].date
    );
    const daysToRepayment = differenceInDays(
        nextRepaymentDate,
        // Get the date without a timestamp to calculate the number of full days
        new Date().toISOString().split('T')[0]
    );
    const nextRepaymentAmount = account.scheduled_payments!.schedule![0].total;

    let dateFnsLocal = 'uk';
    if (locale === 'de-DE') {
        dateFnsLocal = deLocale as string;
    }

    const month = format(nextRepaymentDate, 'MMM', { locale: dateFnsLocal });
    const date = format(nextRepaymentDate, 'D');

    return (
        <>
            <DashboardCTA
                title={<FormattedMessage {...messages.futureRepaymentsTitle} />}
                icon={<DateInIcon month={month} date={date} />}
                data-testid="future-repayments"
                className={className}
                description={
                    <FormattedMessage
                        {...messages.futureRepayments}
                        values={{
                            repaymentAmount: (
                                <strong>
                                    <FormattedCurrency
                                        value={nextRepaymentAmount}
                                    />
                                </strong>
                            ),
                            timeToRepayment: <strong>{daysToRepayment}</strong>
                        }}
                    />
                }
                cta={
                    <Button
                        variant="tertiary"
                        data-testid="check-your-schedule"
                        onClick={() => {
                            showSchedule(productType);
                            tracking.ampli.checkYourScheduleClicked({
                                account_id: account.account_id!
                            });
                            getCountry() === 'DE' &&
                                tracking.ampli.dashboardActionClicked({
                                    name: messages.futureRepaymentsCTA
                                        .defaultMessage
                                });
                        }}
                    >
                        <FormattedMessage {...messages.futureRepaymentsCTA} />
                    </Button>
                }
            />
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(FutureRepayments));
